// API task shape from ProjectTaskSerializer
export interface ApiTask {
  id: number;
  title: string;
  description?: string;
  type: number;
  status: number;
  priority: number;
  easy_id?: number;
  project?: number | null;
  sprint?: number | null;
  kanban_column?: number | null;
  assigned_to: { full_name?: string; email?: string } | null;
  due_date?: string | null;
  estimated_hours?: number | null;
  actual_hours?: number | null;
  labels?: Array<{ id: number; name: string; color: number; color_display: string }>;
  child_count?: number;
  release?: { id: number; name: string } | null;
  card_type: number | null;
  dashboard: number | null;
  stage: number | null;
  data: Record<string, unknown>;
  dashboard_data: Record<string, unknown> | null;
  order?: number;
  sprint_order?: number;
}

// GTM Card Types
export type GTMCardType = 'whitepaper' | 'blog' | 'podcast' | 'landing_page' | 'campaign';

export interface GTMCard {
  id: string;
  title: string;
  type: GTMCardType;
  taskId: string;
  taskIdNum?: number;
  cardTypeNum?: number;
  stage: number;
  assignedTo?: {
    full_name?: string;
    email?: string;
  } | null;
  dashboardData?: Record<string, unknown>;
}

// Customer Card Types
export type CustomerCardType = 'subscriber' | 'registrant' | 'demo' | 'trial' | 'lead' | 'referral' | 'inquiry' | 'account';

export interface CustomerCard {
  id: string;
  title: string;
  type: CustomerCardType;
  company?: string;
  value?: string;
  score?: number;
  status?: string;
  taskId: string;
  taskIdNum: number;
  stage: number;
  assignedTo?: {
    full_name?: string;
    email?: string;
  } | null;
  arrPotential?: number;
  source?: string;
}

// Helper function to map card_type number to GTM string
export function mapCardTypeToString(cardType: number | null | undefined): GTMCardType | null {
  if (cardType === null || cardType === undefined) return null;
  const mapping: { [key: number]: GTMCardType } = {
    1: 'whitepaper',
    2: 'blog',
    3: 'podcast',
    4: 'landing_page',
    5: 'campaign',
  };
  return mapping[cardType] || null;
}

// Helper function to map card type string back to number
export function mapCardTypeToNumber(cardType: GTMCardType): number | null {
  const mapping: { [key: string]: number } = {
    'whitepaper': 1,
    'blog': 2,
    'podcast': 3,
    'landing_page': 4,
    'campaign': 5,
  };
  return mapping[cardType] || null;
}

// Map API task to GTM card format
export function mapToGTMCard(task: ApiTask): GTMCard {
  return {
    id: task.id.toString(),
    title: task.title,
    type: mapCardTypeToString(task.card_type) || 'blog', // Default to blog if mapping fails
    taskId: `#GTM-${task.id}`,
    taskIdNum: task.id,
    cardTypeNum: task.card_type ?? undefined,
    stage: task.stage !== null && task.stage !== undefined ? task.stage : 0,
    assignedTo: task.assigned_to || null,
    dashboardData: task.dashboard_data || task.data || {},
  };
}

// Determine customer card type from backend data
export function determineCustomerCardType(task: ApiTask, dashboardData: Record<string, unknown>): CustomerCardType {
  const cardType = task.card_type;
  const sourceVal = dashboardData.source;
  const source = (typeof sourceVal === 'string' ? sourceVal : '').toLowerCase();

  // Accounts are always accounts
  if (cardType === 9) return 'account';

  // For opportunities (card_type 10), determine type from source
  if (cardType === 10) {
    if (source.includes('newsletter') || source.includes('website')) return 'subscriber';
    if (source.includes('event') || source.includes('webinar')) return 'registrant';
    if (source.includes('demo') || dashboardData.demo_date) return 'demo';
    if (source.includes('trial') || dashboardData.trial_start) return 'trial';
    if (source.includes('referral')) return 'referral';
    if (source.includes('inquiry') || source.includes('contact')) return 'inquiry';
    return 'lead'; // Default for opportunities
  }
  
  return 'lead';
}

// Map API task to Customer card format
export function mapToCustomerCard(task: ApiTask): CustomerCard {
  const dashboardData: Record<string, unknown> = (task.dashboard_data || task.data || {}) as Record<string, unknown>;

  // Determine card type
  const type = determineCustomerCardType(task, dashboardData);

  // Extract fields from dashboard_data
  const company = (dashboardData.company as string) || '';
  const score = (dashboardData.lead_score as number) || (dashboardData.health as number) || 0;
  const source = (dashboardData.source as string) || '';
  
  // Format value
  let value: string | undefined;
  if (type === 'account') {
    const accountValue = (dashboardData.value as number) || (dashboardData.arr_potential as number);
    if (accountValue) {
      value = accountValue >= 1000 ? `$${(accountValue / 1000).toFixed(0)}k/yr` : `$${accountValue}/yr`;
    }
  } else {
    const arrPotential = dashboardData.arr_potential as number;
    if (arrPotential) {
      value = arrPotential >= 1000 ? `$${(arrPotential / 1000).toFixed(0)}k` : `$${arrPotential}`;
    }
  }
  
  return {
    id: task.id.toString(),
    title: task.title,
    type,
    company,
    value,
    score: score || undefined,
    taskId: `#CUS-${task.id}`,
    taskIdNum: task.id,
    stage: task.stage !== null && task.stage !== undefined ? task.stage : 0,
    assignedTo: task.assigned_to || null,
    arrPotential: (dashboardData.arr_potential as number) || 0,
    source,
  };
}

// Product Timeline Card Types
export type ProductCardType = 'product_feature' | 'data_import' | 'manufacturer' | 'distributor' | 'product_line';

export interface ProductCard {
  id: string;
  title: string;
  type: ProductCardType;
  taskId: string;
  taskIdNum: number;
  cardTypeNum: number;
  stage: number;
  quarter: string;
  assignedTo?: {
    full_name?: string;
    email?: string;
  } | null;
  dashboardData?: Record<string, unknown>;
}

// Helper: map card_type number to ProductCardType string
export function mapProductCardTypeToString(cardType: number | null | undefined): ProductCardType | null {
  if (cardType === null || cardType === undefined) return null;
  const mapping: { [key: number]: ProductCardType } = {
    11: 'product_feature',
    12: 'data_import',
    13: 'manufacturer',
    14: 'distributor',
    15: 'product_line',
  };
  return mapping[cardType] || null;
}

// Map API task to Product Timeline card format
export function mapToProductCard(task: ApiTask): ProductCard {
  const dashboardData: Record<string, unknown> = (task.dashboard_data || task.data || {}) as Record<string, unknown>;
  return {
    id: task.id.toString(),
    title: task.title,
    type: mapProductCardTypeToString(task.card_type) || 'product_feature',
    taskId: `#PRD-${task.id}`,
    taskIdNum: task.id,
    cardTypeNum: task.card_type ?? 0,
    stage: task.stage !== null && task.stage !== undefined ? task.stage : 0,
    quarter: (dashboardData.quarter as string) || '',
    assignedTo: task.assigned_to || null,
    dashboardData,
  };
}
